import type { Language } from '@/types';

const DEFAULT_OPTIONS: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };

/**
 * Georgian month names are spelled out here instead of relying on `Intl` with `ka-GE`:
 * several Chromium builds (including the one Playwright ships) have no Georgian locale data
 * and silently fall back to patterns like "2026 M09 18".
 */
const KA_MONTHS_LONG = [
  'იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი',
  'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი',
] as const;

const KA_MONTHS_SHORT = [
  'იან.', 'თებ.', 'მარ.', 'აპრ.', 'მაი.', 'ივნ.',
  'ივლ.', 'აგვ.', 'სექ.', 'ოქტ.', 'ნოე.', 'დეკ.',
] as const;

function formatGeorgian(date: Date, options: Intl.DateTimeFormatOptions): string {
  const monthNames = options.month === 'short' ? KA_MONTHS_SHORT : KA_MONTHS_LONG;
  const month = options.month ? monthNames[date.getMonth()] ?? '' : '';
  const day = options.day ? String(date.getDate()) : '';
  const year = options.year ? String(date.getFullYear()) : '';

  const dayMonth = [day, month].filter(Boolean).join(' ');
  if (!year) return dayMonth;
  if (!dayMonth) return year;
  // "3 მაისი, 2025" for full dates, "3 მაი. 2025" for abbreviated ones, "მაისი, 2025" for month + year.
  return options.month === 'short' && day ? `${dayMonth} ${year}` : `${dayMonth}, ${year}`;
}

/**
 * Format a date for the visitor's language, so Georgian pages show Georgian month names
 * instead of English ones. Invalid input renders as an empty string rather than "Invalid Date".
 */
export function formatDate(
  date: string | number | Date,
  language: Language,
  options: Intl.DateTimeFormatOptions = DEFAULT_OPTIONS,
): string {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  if (language === 'ka') return formatGeorgian(parsed, options);
  return parsed.toLocaleDateString('en-US', options);
}
